/**
 * i18next runtime setup.
 *
 * Imported for its side effect by `src/main.tsx` before the first render, so
 * every component can call `useT()` synchronously — the English catalogue is
 * bundled (see `@/i18n/resources`), so there is no suspense boundary and no
 * flash of untranslated text.
 *
 * Language resolution order:
 *   1. an explicit choice the user made in the switcher, persisted in
 *      localStorage (auto-detection is never written back)
 *   2. `navigator.language` / `navigator.languages`
 *   3. English
 *
 * The document's `lang` attribute is kept in step with the active language so
 * screen readers switch voice, and so `:lang()` styling and browser translation
 * prompts behave.
 */

import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import {
  APP_LANGUAGE_TAGS,
  APP_NAMESPACES,
  DEFAULT_LANGUAGE,
  DEFAULT_NAMESPACE,
  getLanguageDescriptor,
  LANGUAGE_STORAGE_KEY,
  loadLanguageBundle,
  resolveAppLanguage,
  resources,
} from "@/i18n";
import { logger } from "@/lib/logger";

/**
 * Tags we do not ship, mapped onto ones we do; anything else falls back to English.
 *
 * Detected tags are already mapped onto a shipped tag by `resolveAppLanguage`
 * (see `convertDetectedLanguage` below); this table is the safety net for a
 * code that reaches i18next some other way, chiefly a bare `zh` or a script
 * subtag rather than `zh-CN`.
 */
const FALLBACK_MAP: Record<string, string[]> = {
  zh: ["zh-CN", DEFAULT_LANGUAGE],
  "zh-Hans": ["zh-CN", DEFAULT_LANGUAGE],
  "zh-SG": ["zh-CN", DEFAULT_LANGUAGE],
  default: [DEFAULT_LANGUAGE],
};

/** Applies the active language to `<html lang>` — a WCAG 3.1.1 requirement. */
function syncDocumentLanguage(language: string): void {
  if (typeof document === "undefined") {
    return;
  }
  const descriptor = getLanguageDescriptor(language);
  document.documentElement.lang = descriptor.locale;
  // Every language we ship is left-to-right today. Set it explicitly anyway so
  // adding an RTL language is a one-line change here rather than a hunt.
  document.documentElement.dir = "ltr";
}

void i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ns: [...APP_NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    fallbackNS: DEFAULT_NAMESPACE,
    fallbackLng: FALLBACK_MAP,
    supportedLngs: [...APP_LANGUAGE_TAGS],
    // Exact tags only. `nonExplicitSupportedLngs: true` made i18next check just
    // the primary subtag, and since we ship "zh-CN" and "pt-BR" but not "zh"
    // or "pt", picking 简体中文 or Português silently stayed on English (Sam
    // Chen, 8 Oct). Regional browser tags ("de-AT", "en-US") are folded onto
    // our tags by `convertDetectedLanguage` instead.
    nonExplicitSupportedLngs: false,
    load: "currentOnly",
    returnNull: false,
    interpolation: {
      // React escapes for us; double-escaping would render "&amp;" to the user.
      escapeValue: false,
    },
    detection: {
      // No "htmlTag": index.html hardcodes lang="en", which would always match
      // and silently beat the browser's own preference.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // "de-AT" → "de", "zh-Hans" → "zh-CN", "pt-PT" → "pt-BR", "zh-TW" → "en".
      convertDetectedLanguage: (language: string) => resolveAppLanguage(language),
      // Never cache what was merely detected. On a shared PC the detector
      // would otherwise write the first person's browser language to
      // localStorage, and everyone after them would inherit it. Only an
      // explicit pick in the switcher is persisted (see `useLanguage`).
      caches: [],
    },
    react: {
      useSuspense: false,
    },
  });

/**
 * Fetch a language's catalogues and register them, once.
 *
 * Only English ships in the main bundle, so everything else has to arrive
 * before it can be shown. Already-loaded languages return immediately —
 * i18next keeps the bundle, so switching back is free.
 *
 * A failed fetch is swallowed on purpose: the reader keeps English, which is
 * the fallback they would have seen anyway. Throwing here would take the page
 * down over a dropped chunk on a bad connection, which is precisely the
 * network this split exists to serve.
 */
export async function ensureLanguageLoaded(language: string): Promise<void> {
  if (language === DEFAULT_LANGUAGE || i18next.hasResourceBundle(language, DEFAULT_NAMESPACE)) {
    return;
  }
  try {
    const bundle = await loadLanguageBundle(language);
    for (const [namespace, catalogue] of Object.entries(bundle)) {
      i18next.addResourceBundle(language, namespace, catalogue, true, true);
    }
  } catch (error) {
    logger.warn("[i18n] could not load catalogues for", language, error);
  }
}

i18next.on("languageChanged", (language) => {
  logger.debug("[i18n] language changed to", language);
  syncDocumentLanguage(language);
});

/* A language restored from localStorage is resolved before any component
   renders, but its catalogue is not here yet — so fetch it and re-apply.
   Until it lands the reader sees English, which is the fallback rather than
   blank text. */
void ensureLanguageLoaded(i18next.resolvedLanguage ?? i18next.language ?? DEFAULT_LANGUAGE).then(
  () => i18next.changeLanguage(i18next.resolvedLanguage ?? i18next.language),
);

syncDocumentLanguage(i18next.resolvedLanguage ?? i18next.language ?? DEFAULT_LANGUAGE);
logger.debug("[i18n] initialised", i18next.resolvedLanguage ?? i18next.language);

export { i18next };
